import React, { useState, useRef, useEffect } from "react";
import "./TimeInput.scss";
import DownArrow from "../../utils/Images/DownArrow";

const hours = Array.from({ length: 12 }, (_, i) => i + 1);
const minutes = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0"),
);
const ampmOptions = ["AM", "PM"];

interface TimeInputProps {
  onTimeChange: (val: string) => void;
  setAmPm: (val: string) => void;
  amPm: string;
  hour: number;
  setHour: (val: number) => void;
  minute: string;
  setMinute: (val: string) => void;
}

const TimeInput: React.FC<TimeInputProps> = ({
  onTimeChange,
  setAmPm,
  amPm,
  hour,
  setHour,
  minute,
  setMinute,
}) => {
  const [showTimePopover, setShowTimePopover] = useState(false);
  const [showAmpmPopover, setShowAmpmPopover] = useState(false);
  const timePopoverRef = useRef<HTMLDivElement>(null);
  const ampmPopoverRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const ampmRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleTimePopover = () => {
    setShowTimePopover(!showTimePopover);
    setShowAmpmPopover(false);
  };

  const toggleAmpmPopover = () => {
    setShowAmpmPopover(!showAmpmPopover);
    setShowTimePopover(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowTimePopover(false);
        setShowAmpmPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    onTimeChange(`${hour}:${minute}`);
  }, [hour, minute]);

  useEffect(() => {
    const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
      if (ref.current && ref.current.parentElement) {
        const parent = ref.current.parentElement;
        parent.scrollTop =
          ref.current.offsetTop -
          parent.clientHeight / 2 +
          ref.current.clientHeight / 2;
      }
    };

    if (showTimePopover) {
      scrollToRef(hourRef);
      scrollToRef(minuteRef);
    }

    if (showAmpmPopover) {
      scrollToRef(ampmRef);
    }
  }, [showTimePopover, showAmpmPopover]);

  return (
    <div className="timerWrapper" ref={wrapperRef}>
      <div className="timeDisplay">
        <button
          type="button"
          className="timerButton"
          onClick={toggleTimePopover}
        >
          {hour}:{minute}
        </button>
        <button
          type="button"
          className="timerButton"
          onClick={toggleAmpmPopover}
        >
          {amPm}
          <DownArrow />
        </button>
      </div>

      {showTimePopover && (
        <div className="popoverSec" ref={timePopoverRef}>
          <div className="list-group">
            <div className="list-column">
              {hours.map((h) => (
                <div
                  key={h}
                  ref={hour === h ? hourRef : null}
                  className={`list-item ${hour === h ? "active" : ""}`}
                  onClick={() => setHour(h)}
                >
                  {h}
                </div>
              ))}
            </div>

            <div className="list-column">
              {minutes.map((m) => (
                <div
                  key={m}
                  ref={minute === m ? minuteRef : null}
                  className={`list-item ${minute === m ? "active" : ""}`}
                  onClick={() => {
                    setMinute(m);
                    setShowTimePopover(false);
                  }}
                >
                  {m}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAmpmPopover && (
        <div className="popoverSec popoverAMPM" ref={ampmPopoverRef}>
          <div className="list-group">
            <div className="list-column">
              {ampmOptions.map((option) => (
                <div
                  key={option}
                  ref={amPm === option ? ampmRef : null}
                  className={`list-item ${amPm === option ? "active" : ""}`}
                  onClick={() => {
                    setAmPm(option);
                    setShowAmpmPopover(false);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeInput;
